import React, { useState, useEffect, useRef } from "react";
import { 
  FaUser, FaEnvelope, FaPhoneAlt, FaCalendarAlt, FaUsers, 
  FaBed, FaCoffee, FaWifi, FaParking, FaShieldAlt, FaSwimmingPool,
  FaDumbbell, FaUtensils, FaTv, FaWind, FaBath, FaClipboardList,
  FaArrowRight, FaCheckCircle, FaCreditCard, FaRupeeSign,
  FaStar, FaClock, FaMapMarkerAlt, FaWhatsapp, FaPlane
} from "react-icons/fa";

const Booking = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const sectionRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    checkIn: "",
    checkOut: "",
    guests: "2",
    specialRequests: ""
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // EXACT ADDRESS
  const fullAddress = "2, Sri Ram Nagar Colony, Near Kailashpuri Seva Aashram, Mahmoorganj, Ranipur Road, Ranipur Roadin Front Of Banaras Railway Station Road, Chetna Nagar Colony, Varanasi-221106, Uttar Pradesh";

  const amenitiesList = [
    { icon: FaWifi, name: "Free WiFi", color: "text-blue-500" },
    { icon: FaCoffee, name: "Free Breakfast", color: "text-orange-500" },
    { icon: FaParking, name: "Free Parking", color: "text-green-500" },
    { icon: FaShieldAlt, name: "24/7 Security", color: "text-red-500" },
    { icon: FaSwimmingPool, name: "", color: "text-cyan-500" },
    { icon: FaDumbbell, name: "Fitness Center", color: "text-purple-500" },
    { icon: FaUtensils, name: "Restaurant", color: "text-pink-500" },
    { icon: FaTv, name: "Smart TV", color: "text-indigo-500" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          checkIn: "",
          checkOut: "",
          guests: "2",
          specialRequests: ""
        });
      }, 3000);
    }, 2000);
  };

  return (
    <section id="booking" ref={sectionRef} className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white min-h-screen">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Section */}
        <div className={`text-center mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-blue-600 font-semibold tracking-wider text-sm uppercase bg-blue-100 px-4 py-1.5 rounded-full inline-block mb-3">
            Book Your Stay
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-3">
            Reserve Your <span className="text-blue-600">Luxury Room</span>
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Experience unparalleled comfort and elegance. Book directly for the best rates and exclusive offers.
          </p>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className={`mb-8 p-5 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 animate-fade-in transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <FaCheckCircle className="text-green-500 text-2xl" />
            <div>
              <p className="text-green-700 font-semibold text-lg">Booking Confirmed!</p>
              <p className="text-green-600">Thank you for choosing us. A confirmation email has been sent to your inbox.</p>
            </div>
          </div>
        )}

        {/* Main Booking Grid - Without Room Selection */}
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Left Side - Booking Form */}
          <div className={`lg:col-span-2 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaClipboardList className="text-blue-600" />
                Your Details
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <div className="relative">
                      <FaUser className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <div className="relative">
                      <FaEnvelope className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <FaPhoneAlt className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="+91 95550 40589"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Guests *
                    </label>
                    <div className="relative">
                      <FaUsers className="absolute left-3 top-3 text-gray-400" />
                      <select
                        name="guests"
                        value={formData.guests}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="1">1 Guest</option>
                        <option value="2">2 Guests</option>
                        <option value="3">3 Guests</option>
                        <option value="4">4 Guests</option>
                        <option value="5">5 Guests</option>
                        <option value="6">6 Guests</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Check-in Date *
                    </label>
                    <div className="relative">
                      <FaCalendarAlt className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="date"
                        name="checkIn"
                        value={formData.checkIn}
                        onChange={handleChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Check-out Date *
                    </label>
                    <div className="relative">
                      <FaCalendarAlt className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="date"
                        name="checkOut"
                        value={formData.checkOut}
                        onChange={handleChange}
                        required
                        min={formData.checkIn || new Date().toISOString().split('T')[0]}
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Requests (Optional)
                  </label>
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Any special requests or preferences (e.g., extra bed, early check-in, etc.)"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.checkIn || !formData.checkOut}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      Submit Booking Request
                      <FaArrowRight />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Right Side - Info & Amenities */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Why Book With Us?</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-500 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">Best Price Guarantee</p>
                    <p className="text-sm text-gray-500">Direct booking exclusive rates</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-500 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">Free Cancellation</p>
                    <p className="text-sm text-gray-500">Up to 24 hours before check-in</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-500 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">No Hidden Charges</p>
                    <p className="text-sm text-gray-500">Taxes included in price</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="text-green-500 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">Instant Confirmation</p>
                    <p className="text-sm text-gray-500">Get confirmation via email/WhatsApp</p>
                  </div>
                </div>
              </div>

              {/* Amenities */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="font-semibold text-gray-800 mb-3">Hotel Amenities</p>
                <div className="grid grid-cols-2 gap-2">
                  {amenitiesList.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <item.icon className={`${item.color} text-xs`} />
                      <span className="text-gray-600">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Banner */}
        <div className={`mt-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="grid md:grid-cols-4 gap-4 text-center">
            <div>
              <FaClock className="text-2xl mx-auto mb-2" />
              <p className="font-semibold">Best Price Guarantee</p>
              <p className="text-sm text-blue-100">Book directly & save</p>
            </div>
            <div>
              <FaCheckCircle className="text-2xl mx-auto mb-2" />
              <p className="font-semibold">Free Cancellation</p>
              <p className="text-sm text-blue-100">Up to 24 hours before</p>
            </div>
            <div>
              <FaWhatsapp className="text-2xl mx-auto mb-2" />
              <p className="font-semibold">24/7 Support</p>
              <p className="text-sm text-blue-100">Chat with us anytime</p>
            </div>
            <div>
              <FaPlane className="text-2xl mx-auto mb-2" />
              <p className="font-semibold">Airport Transfer</p>
              <p className="text-sm text-blue-100">On request</p>
            </div>
          </div>
        </div>

        {/* Contact Info with EXACT ADDRESS */}
        <div className={`mt-8 text-center transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-gray-600">
            Need help with your booking? Call us directly at 
            <a href="tel:+919555040589" className="text-blue-600 font-semibold ml-1">+91 95550 40589</a>
          </p>
          <p className="text-sm text-gray-400 mt-1 flex items-center justify-center gap-1">
            <FaMapMarkerAlt className="text-red-500" />
            {fullAddress}
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default Booking;